import type { JSX } from "react";
import EntityRow from "@/components/common/EntityRow/EntityRow";
import {
  UNAVAILABLE_KIND_LABELS,
  UNAVAILABLE_REASON_SHORT,
  unavailableKindHeading,
  type UnavailableItem,
  type UnavailableKindGroup,
} from "@utils/viewDefinition/enrichment";
import type { UnresolvableReason } from "@utils/viewDefinition/types";

const Lock = (): JSX.Element => (
  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <g stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4.6" y="8.9" width="10.8" height="7.5" rx="1.7" />
      <path d="M6.8 8.9V6.7a3.2 3.2 0 0 1 6.4 0v2.2" />
    </g>
  </svg>
);

/**
 * References of one kind are usually lost for one reason, and repeating that reason down
 * every row is noise. When the group agrees, the reason is stated once at the top and the
 * rows carry names alone; a mixed group keeps its per-row status column.
 */
function sharedReason(items: UnavailableItem[]): UnresolvableReason | null {
  const first = items[0]?.reason;
  if (!first) return null;
  return items.every((item) => item.reason === first) ? first : null;
}

/**
 * Rows per group under a budget, dealt one at a time in impact order.
 *
 * Every kind is guaranteed at least one row even when the budget is smaller than the number
 * of kinds, because the alternative silently hides a whole kind: a view that lost one filter
 * and fifty brands would show fifty brands and never mention the filter, which is the more
 * consequential loss and the reason UNAVAILABLE_KIND_ORDER ranks it first.
 */
function allocateRows(groups: UnavailableKindGroup[], maxRows?: number): number[] {
  const take = groups.map(() => 0);
  if (!maxRows) return groups.map((group) => group.items.length);

  let remaining = Math.max(maxRows, groups.length);
  let dealt = true;
  while (remaining > 0 && dealt) {
    dealt = false;
    for (let i = 0; i < groups.length && remaining > 0; i += 1) {
      if (take[i] < groups[i].items.length) {
        take[i] += 1;
        remaining -= 1;
        dealt = true;
      }
    }
  }
  return take;
}

const Status = ({ reason }: { reason: UnresolvableReason }): JSX.Element => (
  <span className="inline-flex items-center gap-[5px]">
    <Lock />
    {UNAVAILABLE_REASON_SHORT[reason]}
  </span>
);

const Rows = ({
  items,
  hideStatus,
  divider,
}: {
  items: UnavailableItem[];
  hideStatus?: boolean;
  divider?: boolean;
}): JSX.Element => (
  <>
    {items.map((item, index) => (
      <EntityRow
        key={`${item.kind}-${index}`}
        name={item.displayName}
        tone="caution"
        divider={divider}
        qualifier={hideStatus ? undefined : <Status reason={item.reason} />}
      />
    ))}
  </>
);

export interface UnavailableLedgerProps {
  groups: UnavailableKindGroup[];
  /**
   * "gutter" carries its own kind column and tally, for a surface with nothing around it.
   * "rows" omits both, for a caller whose own section already names and counts the list;
   * a second gutter there would state the same total twice.
   */
  variant?: "gutter" | "rows";
  /** Total rows across all groups. Omit to list everything. */
  maxRows?: number;
}

/**
 * Read-only counterpart to the shared-link ledger: one section per kind, each headed by its
 * tally and, when the whole group agrees, the one reason behind it.
 *
 * Out-of-reach references carry no imagery — a reference the caller cannot see has no logo to
 * fetch — so these rows are deliberately unmarked. An initial chip in place of every logo is a
 * column of grey blobs pretending to be identity, and it costs the names their width.
 */
const UnavailableLedger = ({
  groups,
  variant = "gutter",
  maxRows,
}: UnavailableLedgerProps): JSX.Element => {
  const allocation = allocateRows(groups, maxRows);

  if (variant === "rows") {
    const showKind = groups.length > 1;
    return (
      <>
        {groups.map((group, index) => {
          const reason = sharedReason(group.items);
          const shown = allocation[index];
          const hidden = group.items.length - shown;
          // With one kind the caller's own heading already names and counts it, so the strap
          // exists only to carry an agreed reason. It is dropped when it would add nothing.
          const strap = [
            showKind ? unavailableKindHeading(group.kind, group.items.length) : null,
            reason ? UNAVAILABLE_REASON_SHORT[reason] : null,
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <div key={group.kind} className="mt-[13px] first:mt-0">
              {strap ? (
                <div className="mb-[5px] text-[10px] font-bold uppercase tracking-[0.05em] text-caution-500">
                  {strap}
                </div>
              ) : null}
              <Rows
                items={group.items.slice(0, shown)}
                hideStatus={Boolean(reason)}
                divider={false}
              />
              {hidden > 0 ? (
                <div className="pt-[3px] text-[11px] text-neutral-500 tnum">
                  {`+${hidden} more ${UNAVAILABLE_KIND_LABELS[group.kind].many}`}
                </div>
              ) : null}
            </div>
          );
        })}
      </>
    );
  }

  return (
    <>
      {groups.map((group) => {
        const reason = sharedReason(group.items);
        return (
          <section
            key={group.kind}
            aria-label={UNAVAILABLE_KIND_LABELS[group.kind].many}
            className="grid grid-cols-[100px_1fr] gap-4.5 items-start pt-3.5 mt-3.5 border-t border-neutral-200 first:mt-0 first:pt-0 first:border-t-0"
          >
            <div className="pt-[9px]">
              <div className="text-[11.5px] font-bold text-text-950 tracking-[-0.005em] capitalize">
                {UNAVAILABLE_KIND_LABELS[group.kind].many}
              </div>
              <div className="text-[11px] text-text-50 mt-0.5 tnum">
                {unavailableKindHeading(group.kind, group.items.length)} unavailable
              </div>
              {reason ? (
                <div className="mt-1.5 text-[11px] font-semibold text-caution-500">
                  <Status reason={reason} />
                </div>
              ) : null}
            </div>
            <div className="min-w-0">
              <Rows items={group.items} hideStatus={Boolean(reason)} />
            </div>
          </section>
        );
      })}
    </>
  );
};

export default UnavailableLedger;
